import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";

import s from "./StripeExpressPaymentForm.module.scss";
import { memo, useRef, useState } from "react";
import {
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementReadyEvent,
  StripePaymentElementOptions,
} from "@stripe/stripe-js";
import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { IonToast } from "@ionic/react";
import { useFormsStore } from "../../../../Common/store/common";
import useWebSignupStore from "../store";
import { webSignupCheckoutSessionCache } from "../checkoutSession";
import { WebSignupCheckoutSession } from "../../../utils/webSignupCheckoutSession";
import { getErrorMsg, isMobile } from "../../../../Common/utils/common";
import { isExistingActiveMemberWebSignupError } from "../../../../Common/utils/webSignupExistingMemberError";
import { bugNotif } from "../../../api/bugsnag";
import { WEB_DOWNLOAD_APP } from "../../../constants/routes";
import { getDownloadLandingVersion } from "../../../utils/redirectDownload";
import { getSignupStripeReturnOrigin } from "../../../utils/signupStripeReturnUrl";
import {
  appendWebLogRecordQueryParam,
  identifyWebSignupLogRocketUser,
  prepareWebSignupLogRocketSession,
  stashWebSignupLogRocketSave,
} from "../../../utils/webLogRocketRecord";
import {
  trackSignupCheckout,
  trackSignupProductView,
} from "../../../services/marketing";
import { appendApplovinClickIdsToUrl } from "../../../services/marketing/axon";
import { recordSignUpABTest, trackWebSignUpStep } from "../../../services/user";
import { createSubscriptionExpressCheckout } from "../../../api/stripe";
import { readSignupPreferencePropertiesFromLocation } from "../../../../Common/utils/webSignupLinkParams";
import { createKlaviyoEmail } from "../../../api/klaviyo";
import { trackUserReferral } from "../../../api/user";
import { getStripePriceValueWithPromo } from "../../../services/promo";
import { SIGN_IN_PROVIDER_MAP } from "../../../../Common/models/user";
import { PW_WEB_TEST_CONFIG } from "../../../../Common/testConstants";

const ttqEventSent = false;
const StripeExpressPaymentFormInternal = ({
  onReady,
  setRedirectToApp,
}: {
  onReady: (event: StripeExpressCheckoutElementReadyEvent) => void;
  setRedirectToApp: (data: boolean) => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const setFormLoading = useFormsStore((state) => state.setFormLoading);

  const [error, setError] = useState("");
  const isConfirmInFlightRef = useRef(false);

  const PAYMENT_ELEMENT_OPTIONS = {
    billingDetails: {
      address: "never",
      postalCode: "never",
    },

    terms: {
      card: "never",
      googlePay: "never",
      applePay: "never",
    },
  } as StripePaymentElementOptions;
  const {
    selectedSubscription,

    validStripeCoupon,
    validPromoCode,
  } = useWebSignupStore();

  const onCreateKlaviyoEmail = async (emailAddress: string) => {
    try {
      const subscriptionName = selectedSubscription?.name?.toLowerCase();
      await createKlaviyoEmail(emailAddress, subscriptionName);
    } catch (error) {
      bugNotif("onCreateKlaviyoEmail", getErrorMsg(error).rawErrorMsg);
    }
  };

  const processValidEmail = async (emailAddress: string) => {
    console.log("processValidEmail ----- start", emailAddress);
    const signUpStore = useWebSignupStore.getState();
    const subscription = signUpStore.selectedSubscription;
    const gtId = new URLSearchParams(window.location.search).get("gtId");

    if (subscription !== undefined && !isNull(subscription)) {
      const promoCode = new URLSearchParams(window.location.search).get(
        "promo"
      );
      const promo =
        !isNull(promoCode) && !isEmpty(promoCode)
          ? promoCode.toUpperCase().trim()
          : null;
      await trackSignupProductView({
        emailAddress,
        subscription,
      });

      if (gtId && !isEmpty(gtId)) {
        await recordSignUpABTest({
          gtId,
          identifier: emailAddress,
          revenue: null,
          metadata: {
            subscription,
            promoCode: promo,
          },
        });
      }

      await trackWebSignUpStep({
        emailAddress,
        step: "viewed",
        version: 10,
        subscription: subscription.name,
        promoCode: promo,
        revenue: null,
      });

      const referredByMemberId = new URLSearchParams(
        window.location.search
      ).get("referredByMemberId");

      const referralBonusLookUpKey = signUpStore.referralConfig?.lookUpKey;

      if (
        !isNull(referredByMemberId) &&
        referralBonusLookUpKey !== undefined &&
        !isNull(referralBonusLookUpKey)
      ) {
        const validStripeCoupon = signUpStore.validStripeCoupon;
        const revenue =
          subscription !== undefined &&
          validStripeCoupon !== undefined &&
          !isNull(selectedSubscription)
            ? !isNull(validStripeCoupon)
              ? Math.round(
                  getStripePriceValueWithPromo(
                    subscription.price,
                    validStripeCoupon
                  )
                )
              : Math.round(subscription.price)
            : 0;

        await trackUserReferral({
          referralMemberId: referredByMemberId,
          wlReferralConfigLookupKey: referralBonusLookUpKey,
          referredEmailAddress: emailAddress,
          revenue,
        });
      }
    }

    await onCreateKlaviyoEmail(emailAddress);
    console.log("processValidEmail ----- done", emailAddress);
  };

  const onConfirm = async ({
    paymentFailed,
    billingDetails,
  }: StripeExpressCheckoutElementConfirmEvent) => {
    if (isConfirmInFlightRef.current) {
      paymentFailed({ reason: "fail" });
      return;
    }
    isConfirmInFlightRef.current = true;
    let didCallConfirmPayment = false;

    try {
      if (!stripe) {
        setError("Form is not yet ready. Please try again.");
        paymentFailed({ reason: "fail" });
        return;
      }

      if (isNull(elements)) {
        setError("Something went wrong. Please try again.");
        paymentFailed({ reason: "fail" });
        return;
      }

      if (!selectedSubscription || !selectedSubscription.stripePriceId) {
        setError("Something went wrong. Please try again.");
        paymentFailed({ reason: "fail" });
        return;
      }

      const { email, phone, name } = billingDetails!;

      if (!email) {
        setError("Email address is missing");
        paymentFailed({ reason: "fail" });
        return;
      }

      if (!phone) {
        setError("Phone number is missing");
        paymentFailed({ reason: "fail" });
        return;
      }

      setFormLoading(true);

      let source = "unknown";
      let campaign = "unknown";
      let content = "unknown";
      let medium = "unknown";
      let wlLanding = "unknown";
      const fbSource = new URLSearchParams(window.location.search).get(
        "fbclid"
      );

      if (!isNull(fbSource) && !isEmpty(fbSource)) {
        source = "Facebook";
      }

      const tiktokSource = new URLSearchParams(window.location.search).get(
        "ttclid"
      );

      if (!isNull(tiktokSource) && !isEmpty(tiktokSource)) {
        source = "Tiktok";
      }

      const urlSource = new URLSearchParams(window.location.search).get(
        "utm_source"
      );

      if (!isNull(urlSource) && !isEmpty(urlSource)) {
        source = urlSource;
      }

      const campaignSource = new URLSearchParams(window.location.search).get(
        "utm_campaign"
      );

      if (!isNull(campaignSource) && !isEmpty(campaignSource)) {
        campaign = campaignSource;
      }

      const contentSource = new URLSearchParams(window.location.search).get(
        "utm_content"
      );

      if (!isNull(contentSource) && !isEmpty(contentSource)) {
        content = contentSource;
      }

      const mediumSource = new URLSearchParams(window.location.search).get(
        "utm_medium"
      );

      const wlLandingSource = new URLSearchParams(window.location.search).get(
        "wlLanding"
      );

      if (!isNull(wlLandingSource) && !isEmpty(wlLandingSource)) {
        wlLanding = wlLandingSource;
      }

      if (!isNull(mediumSource) && !isEmpty(mediumSource)) {
        medium = mediumSource;
      }

      const referredByMemberId = new URLSearchParams(
        window.location.search
      ).get("referredByMemberId");

      await processValidEmail(email);
      console.log("will submit element!");

      const { error: submitError } = await elements.submit();
      if (submitError) {
        console.log({
          submitError,
        });
        setError(submitError.message || "");
        setFormLoading(false);
        paymentFailed({ reason: "fail" });
        return;
      }

      console.log("will now create express checkout");

      prepareWebSignupLogRocketSession({
        email,
        name: name || undefined,
        phoneNumber: phone || undefined,
      });

      const checkoutSessionKey = webSignupCheckoutSessionCache.buildKey({
        emailAddress: email,
        phoneNumber: phone,
        priceId: selectedSubscription.stripePriceId!,
        couponId: validStripeCoupon?.id || "",
        promoCode: validPromoCode || "",
        alternativePriceVersion:
          selectedSubscription.alternativePriceVersion ?? "",
        referralBonusLookUpKey:
          useWebSignupStore.getState().referralConfig?.lookUpKey ?? null,
      });

      let checkoutSession: WebSignupCheckoutSession | null =
        webSignupCheckoutSessionCache.get(checkoutSessionKey);

      if (checkoutSession === null) {
        const result = await createSubscriptionExpressCheckout({
          emailAddress: email,
          phoneNumber: phone || "",
          source,
          campaign,
          content,
          medium,
          wlLanding,
          referredByMemberId,
          referralBonusLookUpKey:
            useWebSignupStore.getState().referralConfig?.lookUpKey ?? null,
          signInProvider: phone
            ? SIGN_IN_PROVIDER_MAP.inAppPhoneNumber
            : SIGN_IN_PROVIDER_MAP.inApp,
          priceId: selectedSubscription.stripePriceId!,
          couponId: validStripeCoupon?.id || "",
          promoCode: validPromoCode || "",
          signupPreferenceProperties:
            readSignupPreferencePropertiesFromLocation(),
          ...(selectedSubscription?.alternativePriceVersion !== undefined
            ? {
                alternativePriceVersion:
                  selectedSubscription.alternativePriceVersion,
              }
            : {}),
        });

        console.log("done creating checkout", result);

        checkoutSession = {
          userId: result.data.userId,
          clientSecret: result.data.clientSecret,
          paymentReferenceId: result.data.subscriptionId,
        };

        webSignupCheckoutSessionCache.save({
          checkoutSessionKey,
          session: checkoutSession,
        });

        identifyWebSignupLogRocketUser({
          userId: checkoutSession.userId,
          email,
          name: name || undefined,
          phoneNumber: phone || undefined,
        });
        stashWebSignupLogRocketSave({
          userId: checkoutSession.userId,
          token: result.data.logRocketSaveToken,
        });
      } else {
        console.log("reusing existing express checkout session");
      }

      const { clientSecret, userId } = checkoutSession;

      const emailAddress = email;
      const promoCode = new URLSearchParams(window.location.search).get(
        "promo"
      );
      const promo =
        !isNull(promoCode) && !isEmpty(promoCode)
          ? promoCode.toUpperCase().trim()
          : null;
      const gtId = new URLSearchParams(window.location.search).get("gtId");
      const wldpVersionParam = new URLSearchParams(window.location.search).get(
        "wldpVersion"
      );
      const wldpVersion = getDownloadLandingVersion(wldpVersionParam);
      const parsedUrl = new URL(window.location.href);
      const returnOrigin = getSignupStripeReturnOrigin(parsedUrl);
      // const paramFullName = `${firstName} ${lastName}`.split(" ").join("_");
      const signUpVersion = 10;
      const entriesPerMonthParam =
        selectedSubscription.entriesPerMonth != null
          ? `&entriesPerMonth=${selectedSubscription.entriesPerMonth}`
          : "";
      let returnUrl = `${returnOrigin}${WEB_DOWNLOAD_APP}/?userId=${userId}&pid=${
        selectedSubscription.docId
      }&emailAddress=${encodeURIComponent(emailAddress)}&promoCode=${promo || "null"}&signUpVersion=${signUpVersion}${
        name ? `&signedUpFullName=${name}` : ""
      }&expressCheckout=true&phoneNumber=${encodeURIComponent(phone || "")}&productName=${selectedSubscription.name}&wl-pl=true&wldpVersion=${wldpVersion}${entriesPerMonthParam}${gtId && !isEmpty(gtId) ? `&gtId=${gtId}` : ""}`;

      console.log("return url from express payment -- ", returnUrl);

      if (!isNull(tiktokSource)) {
        returnUrl = `${returnUrl}&ttclid=${tiktokSource}`;
      }

      returnUrl = appendApplovinClickIdsToUrl(returnUrl);

      returnUrl = appendWebLogRecordQueryParam(returnUrl);

      await trackSignupCheckout({
        emailAddress,
        phoneNumber: phone || "",
        subscription: selectedSubscription,
        tiktokSource,
        validStripeCoupon,
      });

      await trackWebSignUpStep({
        emailAddress,
        step: "subscription",
        version: 10,
        subscription: selectedSubscription.name,
        promoCode: promo,
        revenue: null,
      });

      if (!clientSecret) {
        window.location.assign(returnUrl);
        return;
      }

      didCallConfirmPayment = true;
      const { error: errorPayment } = await stripe.confirmPayment({
        elements,
        clientSecret: clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
      });

      if (errorPayment) {
        console.log("got error payment in the end", errorPayment);
        setError(errorPayment.message || "");
      }

      setFormLoading(false);
    } catch (error) {
      setFormLoading(false);
      if (!didCallConfirmPayment) {
        paymentFailed({ reason: "fail" });
      }
      const { errorMsg, rawErrorMsg } = getErrorMsg(error);
      console.log("error here--", {
        errorMsg,
        error,
      });
      if (isExistingActiveMemberWebSignupError(error)) {
        setRedirectToApp(true);
      } else {
        setError(errorMsg);
        bugNotif("StepCompleteProfile", rawErrorMsg);
      }
    } finally {
      isConfirmInFlightRef.current = false;
    }
  };

  return (
    <div
      className={s.paymentContainer}
      data-testid={PW_WEB_TEST_CONFIG.expressForm.paymentContainer}
    >
      <ExpressCheckoutElement
        onReady={onReady}
        onConfirm={onConfirm}
        onClick={({ resolve }) => {
          const options = {
            emailRequired: true,
            phoneNumberRequired: true,
          };
          console.log("will resolve ", options);
          resolve(options);
        }}
        options={{
          layout: {
            overflow: "never",
          },
        }}
      />

      <IonToast
        isOpen={!isEmpty(error)}
        color="danger"
        message={error}
        duration={5000}
      />
    </div>
  );
};

export default memo(StripeExpressPaymentFormInternal);
